import { xpForLevel } from '../content/stats';
import type { World } from './world';
import type { PlayerEnt } from './types';
import { recomputeStats, nextOffer, applyPick } from './player';

export type DuelPhase = 'waiting' | 'draft' | 'countdown' | 'fight' | 'roundEnd' | 'end';

export interface DuelState {
  phase: DuelPhase;
  round: number;
  wins: Map<number, number>;
  phaseUntil: number;
  winner: number;
  order: number[];
}

export const DUEL_LEVEL = 8;
export const DUEL_DRAFTS = 6;

export function initDuel(world: World): DuelState {
  const st: DuelState = { phase: 'waiting', round: 1, wins: new Map(), phaseUntil: 0, winner: 0, order: [] };
  world.duel = st;
  return st;
}

/** Prepares a freshly created player for the duel: fixed level, queued drafts. */
export function duelPreparePlayer(world: World, p: PlayerEnt): void {
  p.level = DUEL_LEVEL;
  p.xp = 0;
  p.xpNext = xpForLevel(DUEL_LEVEL);
  p.draftQueue = [];
  for (let i = 0; i < DUEL_DRAFTS; i++) p.draftQueue.push('skill');
  p.draftQueue.push('keystone');
  recomputeStats(world, p);
  p.hp = p.maxHp;
  const st = world.duel!;
  if (!st.order.includes(p.id)) st.order.push(p.id);
  st.wins.set(p.id, 0);
  placeAtSpawn(world, p);
}

function placeAtSpawn(world: World, p: PlayerEnt): void {
  const st = world.duel!;
  const idx = st.order.indexOf(p.id);
  const sp = world.map.spawnPoints[Math.max(0, idx) % world.map.spawnPoints.length] ?? world.map.center;
  p.x = sp.x;
  p.y = sp.y;
  p.vx = 0;
  p.vy = 0;
  p.knockX = 0;
  p.knockY = 0;
}

function draftDone(p: PlayerEnt): boolean {
  return !p.draft && p.draftQueue.length === 0;
}

function resetForRound(world: World, p: PlayerEnt): void {
  p.dead = false;
  p.hp = p.maxHp;
  p.statuses = [];
  p.shield = 0;
  for (const s of p.actives) s.cd = 0;
  for (const s of p.autos) s.cd = 0;
  p.dashCharges = Math.max(1, Math.floor(p.stats.dashCharges));
  p.dashCd = 0;
  p.invulnUntil = world.tick + world.seconds(3.2);
  for (const sid of p.summons) {
    const s = world.mobs.get(sid);
    if (s) s.dead = true;
  }
  p.summons = [];
  placeAtSpawn(world, p);
}

export function stepDuel(world: World): void {
  const st = world.duel!;
  const players = [...world.players.values()];
  switch (st.phase) {
    case 'waiting':
      if (players.length >= 2) {
        st.phase = 'draft';
        st.phaseUntil = world.tick + world.seconds(60);
      }
      break;
    case 'draft': {
      const done = players.every(draftDone);
      if (done || world.tick >= st.phaseUntil) {
        // force remaining picks synchronously
        for (const p of players) {
          let guard = 0;
          while (!draftDone(p) && guard++ < 40) {
            if (!p.draft) nextOffer(world, p);
            if (p.draft) applyPick(world, p, 0);
            else break;
          }
        }
        st.phase = 'countdown';
        st.phaseUntil = world.tick + world.seconds(3);
        for (const p of players) resetForRound(world, p);
      }
      break;
    }
    case 'countdown':
      if (world.tick >= st.phaseUntil) {
        st.phase = 'fight';
        world.events.push({ t: 'announce', key: 'duel.fight', color: '#ffd166' });
      }
      break;
    case 'fight': {
      const dead = players.filter((p) => p.dead);
      if (dead.length) {
        const alive = players.filter((p) => !p.dead);
        const winner = alive[0];
        if (winner) {
          st.wins.set(winner.id, (st.wins.get(winner.id) ?? 0) + 1);
          if ((st.wins.get(winner.id) ?? 0) >= 2 || players.length < 2) {
            st.phase = 'end';
            st.winner = winner.id;
            st.phaseUntil = world.tick + world.seconds(8);
            world.events.push({ t: 'announce', key: 'duel.victory', vars: { name: winner.name }, color: '#ffd166' });
          } else {
            st.phase = 'roundEnd';
            st.round++;
            st.phaseUntil = world.tick + world.seconds(4);
            world.events.push({ t: 'announce', key: 'duel.roundWon', vars: { name: winner.name }, color: '#ffd166' });
          }
        } else {
          st.phase = 'roundEnd';
          st.round++;
          st.phaseUntil = world.tick + world.seconds(4);
        }
      }
      break;
    }
    case 'roundEnd':
      if (world.tick >= st.phaseUntil) {
        for (const p of players) resetForRound(world, p);
        st.phase = 'countdown';
        st.phaseUntil = world.tick + world.seconds(3);
      }
      break;
    case 'end':
      break;
  }
}
